import HttpClient from "../api/HttpClient";

class OkloReservationsApiClient extends HttpClient {
  constructor(baseURL, token, tenant) {
    super({
      baseURL,
      token,
    });
    this.tenant = tenant;
  }

  get userProfile() {
    return {
      get: () => this.get("user-tenants"),
    };
  }

  get destinations() {
    return {
      get: (prefix) =>
        this.get(`${this.tenant}/lookup/locations?term=${prefix}`),
    };
  }

  get agents() {
    return {
      get: (criteria) => {
        const query = this.createODataQuery(criteria);
        return this.get(`${this.tenant}/agents${query}`);
      },
      getImage: (agentId) => {
        return this.getFile(`${this.tenant}/agents/${agentId}/header-image`, { responseType: 'blob' });
      },
      updateCountry: (agentId, criteria) => {
        return this.put(`${this.tenant}/agents/${agentId}`, criteria);
      },
      post: (agentId, data) => this.post(`${this.tenant}/agents/${agentId}/users`, data),
      uploadImage: (agentId, data) => this.upload("POST", `${this.tenant}/agents/${agentId}/header-image`, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }),
      getEmail: (email) => {
        return this.get(`${this.tenant}/agents/${email}`);
      },
      getLookupAgent: (prefix) => {
        return this.get(`${this.tenant}/lookup/agents?term=${prefix}`);
      }
    };
  }

  get amenities() {
    return {
      get: () => {
        return this.get(`${this.tenant}/amenities`);
      }
    };
  }

  get permissions() {
    return {
      get: () => {
        return this.get(`${this.tenant}/capabilities/my`);
      }
    };
  }

  get locations() {
    return {
      get: (criteria) => {
        return this.get(
          `${this.tenant}/locations${this.createODataQuery(criteria)}`
        );
      },
      getDistance: (criteria) => {
        return this.post(`${this.tenant}/locations/distance`, criteria);
      },
      updateLocationStatus: (locationId, payload) => {
        return this.put(`${this.tenant}/locations/${locationId}/status`, payload);
      },
    };
  }

  get hotels() {
    return {
      post: (criteria) => {
        return this.post(`${this.tenant}/b2b-rates/search/hotels`, criteria);
      },
      postById: (hotelId, criteria) => {
        return this.post(`${this.tenant}/b2b-rates/search/hotels/${hotelId}`, criteria);
      },
      getHotelImages: (propertyId, provider, sessionId) => {
        return this.get(`${this.tenant}/properties/${propertyId}/images?provider=${provider}&sessionId=${sessionId}`);
      },
      getHotels: (criteria) => {
        const query = this.createODataQuery(criteria);
        return this.get(`${this.tenant}/properties${query}`);
      },
      getHotelById: (hotelId) => {
        return this.get(`${this.tenant}/properties/${hotelId}`);
      },
      updateHotelDescription: (hotelId, description) => {
        return this.put(`${this.tenant}/properties/${hotelId}/description`, { description });
      }
    };
  }

  get services() {
    return {
      post: (criteria) => {
        return this.post(`${this.tenant}/b2b-rates/search/services`, criteria);
      },
      postById: (serviceId, criteria) => {
        return this.post(`${this.tenant}/b2b-rates/search/services/${serviceId}`, criteria);
      },
      getServiceImages: (serviceId) => {
        return this.get(`${this.tenant}/services/${serviceId}/images`);
      },
      getServices: (criteria) => {
        const query = this.createODataQuery(criteria);
        return this.get(`${this.tenant}/services${query}`);
      },
      getServiceById: (serviceId) => {
        return this.get(`${this.tenant}/services/${serviceId}`);
      },
      updateServiceDescription: (serviceId, description) => {
        return this.put(`${this.tenant}/services/${serviceId}/description`, { description });
      }
    };
  }

  get transfers() {
    return {
      search: (criteria) => {
        return this.post(`${this.tenant}/b2b-rates/search/transfers`, criteria);
      },
    };
  }

  get reservations() {
    return {
      get: (criteria) => {
        return this.get(
          `${this.tenant}/b2b-reservations${this.createODataQuery(criteria)}`
        );
      },
      getById: (id) => {
        return this.get(`${this.tenant}/b2b-reservations/${id}`);
      },
      generateClientVoucher: (reservationId) => {
        return this.post(`${this.tenant}/reservations/${reservationId}/client-vouchers/`);
      },
      getGeneratedAllClientVoucher: (reservationId, voucherId) => {
        return this.get(`${this.tenant}/reservations/${reservationId}/client-vouchers/${voucherId}`);
      },
      getGeneratedSingleClientVoucher: (reservationId, voucherId, itemReference) => {
        return this.get(`${this.tenant}/reservations/${reservationId}/client-vouchers/${voucherId}/voucher-items/${itemReference}`);
      },
      getClientVoucherList: (reservationId) => {
        return this.get(
          `${this.tenant}/reservations/${reservationId}/client-vouchers`
        );
      },
      updateGuestInfo: (id, payload) => {
        return this.put(`${this.tenant}/b2b-reservations/${id}/paxinfo`, payload);
      },
      syncReservations: (reservationId) => {
        return this.post(`${this.tenant}/b2b-reservations/${reservationId}/sync`);
      },
      createService: (criteria) => {
        return this.post(`${this.tenant}/b2b-reservations/service`, criteria);
      },
      hotelAvailability: (criteria) => {
        return this.post(`${this.tenant}/b2b-reservations/availability`, criteria);
      },
      reserve: (criteria) => {
        return this.post(`${this.tenant}/b2b-reservations/reserve`, criteria);
      },
      removeEntireReservation: (reservationId, criteria) => {
        return this.post(`${this.tenant}/b2b-reservations/${reservationId}/cancel`, criteria);
      },
    };
  }

  get backOfficeReservations() {
    return {
      get: (criteria) => {
        return this.get(
          `${this.tenant}/backoffice-reservations${this.createODataQuery(criteria)}`
        );
      },
      getById: (id) => {
        return this.get(`${this.tenant}/backoffice-reservations/${id}`);
      },
      getConfirmationNumber: (id, provider) => {
        return this.post(`${this.tenant}/backoffice-reservations/${id}/supplier-confirmation?provider=${provider}`);
      },
      updateHotelConfirmation: (id, propertyId, payload) => {
        return this.put(`${this.tenant}/backoffice-reservations/${id}/hotels/${propertyId}/supplier-confirmation`, payload);
      },
      updateServiceConfirmation: (id, serviceId, payload) => {
        return this.put(`${this.tenant}/backoffice-reservations/${id}/services/${serviceId}/supplier-confirmation`, payload);
      },
      updateStatus: (id, payload) => {
        return this.put(`${this.tenant}/backoffice-reservations/${id}/status`, payload);
      },
      updateHotelStatus: (id, payload) => {
        return this.put(`${this.tenant}/backoffice-reservations/${id}/hotels/${payload.PropertyId}/${payload.ItemReference}/status`, payload);
      },
      updateServiceStatus: (id, payload) => {
        return this.put(`${this.tenant}/backoffice-reservations/${id}/services/${payload.PropertyId}/${payload.ItemReference}/status`, payload);
      },
      updateMarkUpHotelRate: (id, propertyId, payload) => {
        return this.put(`${this.tenant}/backoffice-reservations/${id}/hotels/${propertyId}/markup`, payload);
      },
      updateMarkUpServiceRate: (id, serviceId, payload) => {
        return this.put(`${this.tenant}/backoffice-reservations/${id}/services/${serviceId}/markup`, payload);
      },
    };
  }

  get agentUsers() {
    return {
      get: () => {
        return this.get(`${this.tenant}/lookup/agents/my`);
      },
    };
  }

  get countries() {
    return {
      get: () => {
        const query = this.createODataQuery();
        return this.get(
          `${this.tenant}/lookup/countries${query ?? ""}`
        );
      },
    };
  }

  get markups() {
    return {
      createMarkup: (payload) => {
        return this.post(`${this.tenant}/markup`, payload);
      },
      getMarkupById: (id) => {
        return this.get(`${this.tenant}/markup/${id}`);
      },
      updateMarkup: (id, payload) => {
        return this.put(`${this.tenant}/markup/${id}`, payload);
      },
      markupSummaryList: () => {
        return this.get(`${this.tenant}/markup`);
      },
      createMarkupCriteria: (payload) => {
        return this.post(`${this.tenant}/markup/criteria`, payload);
      },
      markupPriority: () => {
        return this.get(`${this.tenant}/lookup/markup-priorities`);
      },
      markupCodes: () => {
        return this.get(`${this.tenant}/lookup/markup-codes`);
      },
      deleteDateBand: (dateBandId) => {
      return this.delete(`${this.tenant}/markup/datebands/${dateBandId}`);
    }
    };
  }

  createODataQuery = (criteria) => {
    criteria = criteria ?? {};

    const filter = criteria.filter ? `$filter=${encodeURIComponent(criteria.filter)}` : "";
    const select = criteria.select ? `$select=${encodeURIComponent(criteria.select)}` : "";
    const orderBy = criteria.orderBy ? `$orderby=${encodeURIComponent(criteria.orderBy)}` : "";
    const top = criteria.top > 0 ? `$top=${criteria.top}` : "";
    const expand = criteria.expand ? `$expand=${encodeURIComponent(criteria.expand)}` : "";
    const apply = criteria.apply ? `$apply=${encodeURIComponent(criteria.apply)}` : "";

    let query = "?";
    const params = [];

    if (top) {
      params.push(top);
    }
    if (filter) {
      params.push(filter);
    }
    if (select) {
      params.push(select);
    }
    if (orderBy) {
      params.push(orderBy);
    }
    if (expand) {
      params.push(expand);
    }
    if (apply) {
      params.push(apply);
    }

    if (params.length > 0) {
      query += params.join('&');
    } else {
      query = null
    }

    return query;
  }

  get chat() {
    return {
      sendMessage: (message) => this.post(`${this.tenant}/ai-reservation/chat`, message),
    };
  }
}

export default OkloReservationsApiClient;
